/**
 * Pure, editor-agnostic formula logic shared between the draft-js `FormulaInput` and the
 * Tiptap-based replacement. Intentionally contains **no** draft-js / Tiptap imports so it can
 * be consumed by either editor without pulling an editor framework into the bundle.
 *
 * The legacy `../util.tsx` re-exports everything here for backward compatibility, so existing
 * importers (`FormulaInput/util`) keep working unchanged.
 */
import Mexp from 'math-expression-evaluator';
import { Variable } from 'src/api/types';

const mexp = new Mexp();

export const DEFAULT_FORMULA_ERROR_MESSAGE = 'Complete the expression';
export const INVALID_SYNTAX_FORMULA_ERROR_MESSAGE = 'Invalid syntax';

/**
 * Check if a variable ID is from workspace/global source (higher ID ranges).
 * - Global variables: 500001-500999
 * - Workspace variables: 100001+ (but not in global range)
 * - Assessment variables: typically 1-99999 (lower IDs)
 */
const isWorkspaceOrGlobalId = (id: number): boolean => {
  return id >= 100001;
};

export const isWorkspaceOrGlobalVariable = (v: Variable): boolean => {
  return v.workspace_variable || v.isGlobal || isWorkspaceOrGlobalId(v.id);
};

export const normalizeVariableToken = (value?: string | number | null): string => {
  return value?.toString().replace(/[\s_]/g, '').toLowerCase() || '';
};

export const customSuggestionsFilter = (search: string, suggestions: Variable[]): Variable[] => {
  const searchLower = search.toLowerCase();

  const textFiltered = suggestions.filter(
    (suggestion) =>
      (suggestion.alias && suggestion.alias.toLowerCase().includes(searchLower)) ||
      suggestion.name.toLowerCase().includes(searchLower),
  );

  // Then, filter out workspace/global variables that have overrides.
  // When an override exists (assessment variable with same name), we should only show the override.
  const filteredResults = textFiltered.filter((suggestion) => {
    if (isWorkspaceOrGlobalVariable(suggestion)) {
      const hasOverride = textFiltered.some(
        (s) => s.name === suggestion.name && s.id !== suggestion.id && s.id < suggestion.id,
      );
      if (hasOverride) {
        return false;
      }
    }
    return true;
  });

  return filteredResults;
};

/**
 * Find a variable by ID, with override support.
 * If the variable found is a workspace/global variable AND there's an override (assessment variable)
 * with the same name, return the override instead.
 *
 * Note: Some workspace variables have incorrect flags (workspace_variable: false, isGlobal: false)
 * due to backend data issues. We detect these by:
 * 1. Checking the ID range (workspace/global IDs are >= 100001)
 * 2. Looking for duplicate names with different IDs (lower ID = override)
 */
export const findVariableWithOverride = (data: Variable[], id: number): Variable | undefined => {
  const directMatch = data.find((v) => v.id === id);

  if (!directMatch) {
    return undefined;
  }

  if (isWorkspaceOrGlobalVariable(directMatch)) {
    const override = data.find((v) => v.name === directMatch.name && v.id !== directMatch.id && v.id < directMatch.id);

    if (override) {
      return override;
    }
  }

  return directMatch;
};

export const findVariableByToken = (data: Variable[], token: string): Variable | undefined => {
  if (!token) {
    return undefined;
  }

  if (/^\d+$/.test(token)) {
    return findVariableWithOverride(data, Number(token));
  }

  const normalizedToken = normalizeVariableToken(token);
  return data.find((variable) => {
    const normalizedId = normalizeVariableToken(variable.id);
    const normalizedAlias = normalizeVariableToken(variable.alias);
    const normalizedName = normalizeVariableToken(variable.name);

    return (
      normalizedId === normalizedToken ||
      normalizedAlias === normalizedToken ||
      normalizedName === normalizedToken
    );
  });
};

/** Variable tokens referenced in a formula, e.g. "#100 * #2" → ["100", "2"]. */
export const getFormulaVariableTokens = (formula?: string | number | null): string[] => {
  if (formula == null) return [];
  return (String(formula).match(/#([A-Za-z0-9_]+)/g) ?? []).map((token) => token.slice(1));
};

/**
 * True when the formula references at least one variable that is missing in this
 * workspace — either unresolved (deleted/absent) or resolved but with no value.
 * Uses the same `findVariableByToken` resolution (incl. overrides) as the chips,
 * so this flags exactly when a variable chip would render as "unpopulated".
 */
export const formulaReferencesUnpopulatedVariable = (
  formula: string | number | null | undefined,
  variables: Variable[],
): boolean => {
  return getFormulaVariableTokens(formula).some((token) => {
    const variable = findVariableByToken(variables, token);
    return !variable || variable.value == null;
  });
};

export const formatExpression = (expression?: string | null) => {
  if (!expression) return '';

  expression = expression.replace(/\s+/g, '');
  expression = expression.replace(/(#[A-Za-z0-9_]+|\d+(\.\d+)?|\+|\-|\*|\/|\(|\))/g, ' $1 ').trim();
  expression = expression.replace(/\s+/g, ' ');

  return expression;
};

export const expressionToFormula = (inputStr: string, variables: Variable[]): string => {
  let result = inputStr;

  // Build a map of variable IDs to their effective values (considering overrides)
  const idToValueMap = new Map<number, number>();

  for (const variable of variables) {
    if (isWorkspaceOrGlobalVariable(variable)) {
      const override = variables.find((v) => v.name === variable.name && v.id !== variable.id && v.id < variable.id);
      if (override) {
        idToValueMap.set(variable.id, override.value ?? 0);
      } else {
        idToValueMap.set(variable.id, variable.value ?? 0);
      }
    } else {
      idToValueMap.set(variable.id, variable.value ?? 0);
    }
  }

  for (const [id, value] of idToValueMap) {
    const regex = new RegExp(`#${id}\\b`, 'g');
    result = result.replace(regex, value.toString());
  }

  return result;
};

export const validateFormulaSyntax = (formula?: string | null) => {
  if (!formula) return true;

  if (/(^|\s|\W)\.\d+/.test(formula)) {
    return false;
  }

  try {
    mexp.lex(formula, []);
    return true;
  } catch (error) {
    return false;
  }
};

export const evaluate = async (formula: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    try {
      const invalidNumberFormat = /(^|\s|\W)\.\d+/;

      if (invalidNumberFormat.test(formula)) {
        throw new Error('Invalid formula');
      }

      const val = mexp.eval(formula, [], {});

      if (isNaN(val) || val === Infinity || val === -Infinity) {
        throw new Error('Invalid formula');
      }

      const _areParenthesesBalanced = (expression: string): boolean => {
        let stack: string[] = [];

        for (let char of expression) {
          if (char === '(') {
            stack.push(char);
          } else if (char === ')') {
            if (stack.length === 0) {
              return false;
            }
            stack.pop();
          }
        }

        return stack.length === 0;
      };

      if (!_areParenthesesBalanced(formula)) {
        throw new Error('Invalid formula');
      }

      resolve(val);
    } catch (error) {
      reject(error);
    }
  });
};
